import type {
  CountsJson,
  RoutesJson,
  ImportPagesPayload,
  ImportCountsPayload,
  ImportRoutesPayload,
  PageSettings,
} from '../../types/floorPlan.types';
import type {
  RawDiagramItem,
  TransformedDiagramItem,
} from '../../types/diagram.types';
import { slugify } from '../slugify';

export const transformPages = (
  countsJson: CountsJson,
  pageSettingsMap: Record<number, PageSettings>,
  projectId: string,
  planName: string,
): ImportPagesPayload => {
  const sluggedPlanName = slugify(planName);

  return {
    pages: (countsJson.pages || []).map((p) => {
      const settings = pageSettingsMap[p.index];
      const ext = settings?.outputFormat || 'png';
      const basePath = `smart-surveyor/projects/${projectId}/floor_plans/${sluggedPlanName}/source/page_${p.index}`;

      return {
        index: p.index,
        image: `${basePath}.${ext}`,
        filePath: `${basePath}.pdf`,
        widthPx: p.width_px || 0,
        heightPx: p.height_px || 0,
        imageScaleFactor: settings?.scaleFactor ?? 1,
      };
    }),
  };
};

export const transformCounts = (
  countsJson: CountsJson,
): ImportCountsPayload => {
  return {
    groups: (countsJson.groups || []).map((g) => ({
      name: g.name,
      markers: (g.markers || []).map((m) => ({
        page: m.page,
        bbox: m.bbox,
      })),
      lvMatch: g.lvMatch
        ? {
            lvId: g.lvMatch.lvId,
            lvName: g.lvMatch.lvName,
            reasoning: g.lvMatch.reasoning || undefined,
          }
        : undefined,
    })),
  };
};

export const transformRoutes = (
  routesJson: RoutesJson,
): ImportRoutesPayload => {
  return {
    routes: (routesJson.routes || []).map((r) => ({
      page: r.page,
      cableType: r.cable_type || '',
      name: r.name || undefined,
      breaker: r.breaker || undefined,
      distancePx: r.distance_px ?? r.distancePx ?? 0,
      meters: r.meters ?? 0,
      nodes: (r.nodes || []).map((n) => ({ id: n.id, x: n.x, y: n.y })),
      edges: (r.edges || []).map((e) => ({ from: e.from, to: e.to })),
      lvMatch: r.lvMatch
        ? {
            lvId: r.lvMatch.lvId,
            lvName: r.lvMatch.lvName,
            reasoning: r.lvMatch.reasoning || undefined,
          }
        : undefined,
    })),
  };
};

export const transformDiagramItems = (
  items: RawDiagramItem[],
): { items: TransformedDiagramItem[] } => {
  return {
    items: items.map((item) => ({
      item: item.item,
      text: item.text || '',
      quantity: item.quantity || 0,
      unit: item.unit || '',
      diagrams: (item.diagrams || []).map((d) => ({
        diagram: d.diagram,
        count: d.count || 0,
        pages: (d.pages || []).map((p) => ({
          page: p.page,
          count: p.count || 0,
          breakerIds: p.breaker_ids || p.breakerIds || [],
        })),
      })),
      lvMatch: item.lvMatch
        ? {
            lvId: item.lvMatch.lvId || undefined,
            lvName: item.lvMatch.lvName || undefined,
            confidence: item.lvMatch.confidence || undefined,
            reasoning: item.lvMatch.reasoning || undefined,
          }
        : undefined,
    })),
  };
};
